import { lazy, Suspense } from "react";
import "./site.css";
import "./redesign.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./components/redesign/HomeRedesign";
const VerticalPage = lazy(() => import("./components/VerticalPage"));
const ServicePage = lazy(() => import("./components/ServicePage"));
const Calculator = lazy(() => import("./components/Calculator"));
const CountryPage = lazy(() => import("./components/CountryPage"));
const ClusterPage = lazy(() => import("./components/ClusterPage"));
const ComboPage = lazy(() => import("./components/ComboPage"));
const InsightsHub = lazy(() => import("./components/InsightsHub"));
const InsightPage = lazy(() => import("./components/InsightPage"));
const CaseStudiesHub = lazy(() => import("./components/redesign/CaseStudiesHub"));
const CaseStudyPage = lazy(() => import("./components/redesign/CaseStudyPage"));
const SourcingHealthCheck = lazy(() => import("./components/redesign/SourcingHealthCheck"));
const WhatWeSourceHub = lazy(() => import("./components/redesign/WhatWeSourceHub"));
const CategoryDetail = lazy(() => import("./components/redesign/CategoryDetail"));
const MarketPage = lazy(() => import("./components/redesign/MarketPage"));
const MarketHub = lazy(() => import("./components/redesign/MarketHub"));
const ProductMarketPage = lazy(() => import("./components/redesign/ProductMarketPage"));
const TextileHub = lazy(() => import("./components/redesign/TextileHub"));
const TextileSubPage = lazy(() => import("./components/redesign/TextileSubPage"));
const SteelMarketPage = lazy(() => import("./components/redesign/SteelMarketPage"));
const CategoryPage = lazy(() => import("./components/redesign/CategoryPage"));
const GarmentPage = lazy(() => import("./components/redesign/GarmentPage"));
const AdminSeo = lazy(() => import("./components/admin/AdminSeo"));
const AdminAssistant = lazy(() => import("./components/admin/AdminAssistant"));
const AssistantPage = lazy(() => import("./components/assistant/AssistantPage"));
const GGOverview = lazy(() => import("./components/garment/GGOverview"));
const GGCountryPage = lazy(() => import("./components/garment/GGCountryPage"));
const GGCompare = lazy(() => import("./components/garment/GGCompare"));
const GGEnquiryPage = lazy(() => import("./components/garment/GGEnquiryPage"));
const AuthCallback = lazy(() => import("./components/admin/AuthCallback"));
const NotFound = lazy(() => import("./components/NotFound"));
import { MOVED_CATEGORIES } from "./departments";
import { CORE_CATEGORY_PATHS, GARMENT_PATHS, STEEL_MARKET_PATHS, PET_MARKET_PATHS, HUB_SLUGS, SUB_SLUGS, INDUSTRY_REDIRECTS, CATEGORY_PAGE_SLUGS, COUNTRY_TO_MARKET, GG_COUNTRY_PATHS } from "./routeIndex";

// 301-style redirect: old /countries/:slug -> new /<slug>-india-sourcing (shipped markets only)
function CountryToMarket() {
  const { slug } = useParams();
  const target = COUNTRY_TO_MARKET[slug];
  return target ? <Navigate to={"/" + target} replace /> : <CountryPage />;
}

// 301-style redirect: old /industries/:slug -> new canonical vertical slug
function IndustryRedirect() {
  const { slug } = useParams();
  const target = INDUSTRY_REDIRECTS[slug];
  return target ? <Navigate to={"/" + target} replace /> : <NotFound />;
}

// /categories/:slug -> canonical vertical page (single canonical, no duplicate content)
const CATEGORY_MAP = {
  "home-decor": "home-decor-manufacturers-india",
  "furniture": "furniture-manufacturers-india",
  "home-furnishing": "home-furnishing-manufacturers-india",
  "garments": "garment-manufacturers-india",
  "textiles": "home-furnishing-manufacturers-india",
  "carpets-rugs": "carpet-manufacturers-india",
  "stainless-steel-utensils": "stainless-steel-utensil-manufacturers-india",
  "cutlery": "cutlery-manufacturers-india",
  "plastic-products": "plastic-products-manufacturers-india",
  "handicrafts": "handicraft-manufacturers-india",
  "leather-products": "leather-products-manufacturers-india",
  "brass-products": "handicraft-manufacturers-india",
  "metal-products": "home-decor-manufacturers-india",
  "wood-products": "furniture-manufacturers-india",
};
function CategoryRedirect() {
  const { slug } = useParams();
  // Door-hardware categories are real pages rendered by VerticalPage
  if (CATEGORY_PAGE_SLUGS.includes(slug)) {
    return <VerticalPage />;
  }
  const target = CATEGORY_MAP[slug];
  if (target) return <Navigate to={"/" + target} replace />;
  return <NotFound />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <Suspense fallback={null}><AuthCallback /></Suspense>;
  const { pathname, search, hash } = location;
  const norm = pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : pathname;
  if (norm !== pathname) return <Navigate to={norm + search + hash} replace />;
  return (
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/industries/:slug" element={<IndustryRedirect />} />
            <Route path="/categories/:slug" element={<CategoryRedirect />} />
            <Route path="/countries/:slug" element={<CountryToMarket />} />
            <Route path="/international-sourcing" element={<MarketHub />} />
            <Route path="/europe-india-sourcing" element={<MarketHub />} />
            <Route path="/middle-east-india-sourcing" element={<MarketHub />} />
            <Route path="/usa-india-sourcing" element={<MarketPage />} />
            <Route path="/uk-india-sourcing" element={<MarketPage />} />
            <Route path="/australia-india-sourcing" element={<MarketPage />} />
            <Route path="/south-africa-india-sourcing" element={<MarketPage />} />
            <Route path="/germany-india-sourcing" element={<MarketPage />} />
            <Route path="/france-india-sourcing" element={<MarketPage />} />
            <Route path="/netherlands-india-sourcing" element={<MarketPage />} />
            <Route path="/sweden-india-sourcing" element={<MarketPage />} />
            <Route path="/denmark-india-sourcing" element={<MarketPage />} />
            <Route path="/italy-india-sourcing" element={<MarketPage />} />
            <Route path="/spain-india-sourcing" element={<MarketPage />} />
            <Route path="/finland-india-sourcing" element={<MarketPage />} />
            <Route path="/uae-india-sourcing" element={<MarketPage />} />
            <Route path="/saudi-arabia-india-sourcing" element={<MarketPage />} />
            <Route path="/qatar-india-sourcing" element={<MarketPage />} />
            <Route path="/kuwait-india-sourcing" element={<MarketPage />} />
            <Route path="/bahrain-india-sourcing" element={<MarketPage />} />
            <Route path="/oman-india-sourcing" element={<MarketPage />} />
            <Route path="/clusters/:slug" element={<ClusterPage />} />
            <Route path="/sourcing/:country/:category" element={<ComboPage />} />
            <Route path="/insights" element={<InsightsHub />} />
            <Route path="/insights/:slug" element={<InsightPage />} />
            <Route path="/case-studies" element={<CaseStudiesHub />} />
            <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
            <Route path="/sourcing-stress-test" element={<SourcingHealthCheck />} />
            <Route path="/sourcing-health-check" element={<Navigate to="/sourcing-stress-test" replace />} />
            <Route path="/what-we-source" element={<WhatWeSourceHub />} />
            {Object.entries(MOVED_CATEGORIES).map(([k, to]) => <Route key={k} path={"/what-we-source/" + k} element={<Navigate to={to} replace />} />)}
            <Route path="/what-we-source/:slug" element={<CategoryDetail />} />
            <Route path="/tools/sourcing-commission-calculator" element={<Calculator />} />
            {[...STEEL_MARKET_PATHS, ...PET_MARKET_PATHS].map((p) => <Route key={p} path={p} element={<SteelMarketPage />} />)}
            {CORE_CATEGORY_PATHS.map((p) => <Route key={p} path={p} element={<CategoryPage />} />)}
            {GARMENT_PATHS.map((p) => <Route key={p} path={p} element={<GarmentPage />} />)}
            <Route path="/global-garment-sourcing" element={<GGOverview />} />
            <Route path="/garment-sourcing-country-comparison" element={<GGCompare />} />
            <Route path="/garment-sourcing-enquiry" element={<GGEnquiryPage />} />
            {GG_COUNTRY_PATHS.map((p) => <Route key={p} path={p} element={<GGCountryPage />} />)}
            <Route path="/admin/seo" element={<AdminSeo />} />
            <Route path="/admin/assistant" element={<AdminAssistant />} />
            <Route path="/ai-sourcing-assistant" element={<AssistantPage />} />
            <Route path="/:market/:combo" element={<ProductMarketPage />} />
            <Route path="/sourcing-company-india" element={<ServicePage />} />
            <Route path="/indian-manufacturers" element={<ServicePage />} />
            <Route path="/source-from-india" element={<ServicePage />} />
            <Route path="/india-manufacturing-partner" element={<ServicePage />} />
            <Route path="/india-sourcing-agent" element={<ServicePage />} />
            <Route path="/india-sourcing-company" element={<ServicePage />} />
            <Route path="/product-sourcing-india" element={<ServicePage />} />
            <Route path="/factory-sourcing-india" element={<ServicePage />} />
            <Route path="/supplier-verification-india" element={<ServicePage />} />
            <Route path="/factory-audit-india" element={<ServicePage />} />
            <Route path="/quality-control-india" element={<ServicePage />} />
            <Route path="/product-development-india" element={<ServicePage />} />
            <Route path="/private-label-manufacturing-india" element={<ServicePage />} />
            <Route path="/oem-manufacturing-india" element={<ServicePage />} />
            <Route path="/export-management-india" element={<ServicePage />} />
            {HUB_SLUGS.map((s) => <Route key={s} path={"/" + s} element={<TextileHub />} />)}
            {SUB_SLUGS.map((s) => <Route key={s} path={"/" + s} element={<TextileSubPage />} />)}
            <Route path="/:slug" element={<VerticalPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
